import React from 'react';
import { Plus, Minus, Printer, Send, X, Check, Clock, ChefHat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Order, OrderItem } from '@/types';
import { formatCurrency } from '@/lib/utils';
import { calculateSubtotal, calculateDiscountValue, calculateOrderTotal } from '@/services/orderApiService';

interface OrderCardProps {
  order: Order;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onAddItems: () => void;
  onSendToKitchen: () => void;
  onPrintOrder: () => void;
  onCancelItem: (itemId: string) => void;
  isSelected?: boolean;
  onClick?: () => void;
}

const OrderCard: React.FC<OrderCardProps> = ({
  order,
  onUpdateQuantity,
  onAddItems,
  onSendToKitchen,
  onPrintOrder,
  onCancelItem,
  isSelected,
  onClick,
}) => {
  const getItemStatusBadge = (item: OrderItem) => {
    const status = item.status || 'pending';
    
    switch (status) {
      case 'pending':
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            <Clock className="h-3 w-3 mr-1" />
            Pendente
          </Badge>
        );
      case 'preparing':
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            <ChefHat className="h-3 w-3 mr-1" />
            Preparando
          </Badge>
        );
      case 'delivered':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <Check className="h-3 w-3 mr-1" />
            Entregue
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
            {status}
          </Badge>
        );
    }
  };

  const getItemStatusColor = (item: OrderItem) => {
    const status = item.status || 'pending';
    
    switch (status) {
      case 'pending':
        return 'border-l-yellow-400';
      case 'preparing':
        return 'border-l-blue-400';
      case 'delivered':
        return 'border-l-green-400';
      default:
        return 'border-l-gray-400';
    }
  };

  const getOrderStatusClass = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'paid':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getOrderStatus = (status: string) => {
    switch (status) {
      case 'open':
        return 'Aberto';
      case 'paid':
        return 'Pago';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const subtotal = calculateSubtotal(order);
  const discountValue = calculateDiscountValue(order);
  const total = calculateOrderTotal(order);

  return (
    <Card 
      className={`transition-all duration-200 hover:shadow-md ${
        isSelected ? 'ring-2 ring-primary border-primary' : ''
      } ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <span className="text-lg">Pedido #{order.id.substring(0, 8)}</span>
          <div className="flex items-center gap-2">
            <Badge className={getOrderStatusClass(order.status)}>
              {getOrderStatus(order.status)}
            </Badge>
            <span className="text-sm text-muted-foreground">
              {new Date(order.createdAt).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Itens do pedido */}
        <div className="space-y-3">
          {order.items.map((item) => (
            <div 
              key={item.id} 
              className={`flex items-center justify-between p-3 bg-muted/50 rounded-lg border-l-4 ${getItemStatusColor(item)}`}
            >
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium">{item.product.name}</h4>
                  {getItemStatusBadge(item)}
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdateQuantity(item.id, Math.max(0, item.quantity - 1));
                      }}
                      disabled={order.status !== 'open'}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    
                    <span className="w-8 text-center font-medium">{item.quantity}</span>
                    
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdateQuantity(item.id, item.quantity + 1);
                      }}
                      disabled={order.status !== 'open'}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {formatCurrency(item.product.price * item.quantity)}
                    </span>
                    
                    {order.status === 'open' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCancelItem(item.id);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Resumo financeiro */}
        <div className="border-t pt-3 space-y-2">
          <div className="flex justify-between text-sm">
            <span>Subtotal:</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          
          {order.discount && order.discount > 0 && (
            <div className="flex justify-between text-sm text-green-600">
              <span>
                Desconto ({order.discountType === 'percentage' ? `${order.discount}%` : 'fixo'}):
              </span>
              <span>-{formatCurrency(discountValue)}</span>
            </div>
          )}
          
          <div className="flex justify-between font-bold text-lg border-t pt-2">
            <span>Total:</span>
            <span>{formatCurrency(total)}</span>
          </div>
        </div>

        {/* Ações do pedido */}
        {order.status === 'open' && (
          <div className="flex gap-2 pt-2">
            <Button
              variant="default"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onAddItems();
              }}
              className="flex-1 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-white font-medium shadow-sm hover:shadow-md transition-all duration-200"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Item
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onPrintOrder();
              }}
            >
              <Printer className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OrderCard;